import {
  Button,
  Container,
  Divider,
  FlexGrid,
  Header,
  Picture,
  Text,
  Visibility,
} from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { localyticsEvent } from '../../../lib/utils';
import ReactCriteo from '../../../lib/utils/criteo';
import { ANALYTICS_EVENTS, COUNTRY_CODES } from '../../constants';
import * as TESTIMONIALS from '../../constants/testimonials';
import AnimatedPhone from '../AnimatedPhone';
import AppPromo from '../AppPromo';
import HomePageGasBackBrands from '../FeaturedGasbackBrands/HomePageGasBackBrands';
import GetTheAppButton from '../GetTheAppButton';
import HomeScreenCardModal from '../HomeScreenCardModal';
import MobileDownloadAd from '../MobileDownloadAd';
import Page from '../Page';
import { NO_TOP_AD } from '../Page/Templates';
import ReachMetrics from '../ReachMetrics';
import SavingsTools from '../SavingsTools';
import SearchStateCloud from '../SearchStateCloud';
import Testimonials from '../Testimonials';
import TestimonialCard from '../Testimonials/TestimonialCard';
import styles from './HomePage.module.css';

const cx = classnames.bind(styles);

const getImageName = isUSUser => (isUSUser ? 'iphone_X_fuel_better' : 'iphone_X_list_CA_020520');

const PHONE_IMAGE_ALT_TEXT = 'iPhone X With GasBuddy App';

function HeroPhoneImage({
  isUSUser,
  ...rest
}) {
  const imageName = getImageName(isUSUser);

  return (
    <Picture
      alt={PHONE_IMAGE_ALT_TEXT}
      srcSet={`
        https://static.gasbuddy.com/web/consumer/phones/${imageName}@2x.png 2x,
        https://static.gasbuddy.com/web/consumer/phones/${imageName}@3x.png 3x
      `}
      src={`https://static.gasbuddy.com/web/consumer/phones/${imageName}.png`}
      {...rest}
    >
      <Picture.Source
        srcSet={`
          https://static.gasbuddy.com/web/consumer/phones/${imageName}.webp,
          https://static.gasbuddy.com/web/consumer/phones/${imageName}@2x.webp 2x,
          https://static.gasbuddy.com/web/consumer/phones/${imageName}@3x.webp 3x
        `}
        type="image/webp"
      />
    </Picture>
  );
}

HeroPhoneImage.propTypes = {
  isUSUser: PropTypes.bool,
};

HeroPhoneImage.defaultProps = {
  isUSUser: false,
};

export default function Homepage({
  country,
  offers,
  payNgHost,
  platformTotals,
  savedByUser,
}) {
  const testimonials = TESTIMONIALS[country] || TESTIMONIALS.CA;
  const isUSUser = country === COUNTRY_CODES.USA;

  testimonials.sort((a, b) => b.id - a.id);

  const subLeadText = isUSUser
    ? {
      bold: 'Save up to 25¢/gal on every fill-up',
      regular: ', everywhere, every time with GasBuddy. Never pay full price for gas again.',
    }
    : {
      bold: '',
      regular: 'Fuel better by always knowing what the cheapest price is and where the best stations are.',
    };

  useEffect(() => {
    ReactCriteo.event({
      event: 'viewHome',
    });
  }, []);

  const handleEnrollClick = useCallback(() => {
    localyticsEvent(ANALYTICS_EVENTS.PAY_CTA_CLICKED, {
      'Screen Name': 'Home',
      Section: 'PayPromo',
    });
  }, []);

  const handleDownloadAppClick = useCallback(() => {
    localyticsEvent(ANALYTICS_EVENTS.APP_STORE_LINK_CLICK, {
      Store: 'Unknown',
      'Screen Name': 'Home',
      Section: 'Mobile Top App Promo',
    });
  }, []);

  let helmetTitle = 'GasBuddy - Cheapest Gas Station Finder App with Money Saving Benefits';
  let helmetDescription = 'Download the free GasBuddy app to find the cheapest gas stations near you, and save up to 40¢/gal by upgrading to a Pay with GasBuddy fuel rewards program.';

  if (country === COUNTRY_CODES.CANADA) {
    helmetTitle = 'GasBuddy - Cheapest Gas and Gas Station Locator in Canada';
    helmetDescription = 'GasBuddy is a community of drivers sharing real-time gas prices through our website and mobile app to help each other find the cheapest nearby gas stations.';
  } else if (country === COUNTRY_CODES.AUSTRALIA) {
    helmetTitle = 'GasBuddy - Cheapest Gas and Gas Station Locator in Australia';
    helmetDescription = 'GasBuddy is a community of drivers sharing real-time gas prices through our website and mobile app to help each other find the cheapest nearby gas stations.';
  }

  return (
    <div className={cx('container')}>
      <Page name="Home" {...NO_TOP_AD} compact>
        <Helmet>
          <title>{helmetTitle}</title>
          <meta property="og:title" content={helmetTitle} />
          <meta name="twitter:title" content={helmetTitle} />
          <meta name="description" content={helmetDescription} />
          <meta property="og:description" content={helmetDescription} />
          <meta name="twitter:description" content={helmetDescription} />
          <link rel="alternate" href="https://www.gasbuddy.com" hrefLang={`en-${country.toLowerCase()}`} />
        </Helmet>
        <HomeScreenCardModal />
        <div className={cx('content')}>
          <div className={cx('hero')}>
            <Visibility desktop={false} tablet={false}>
              <Container>
                <MobileDownloadAd analyticsScreenName="Home_Page" />
                <br />
                <GetTheAppButton cta={false} onClick={handleDownloadAppClick}>
                  Download The App
                </GetTheAppButton>
                <AnimatedPhone country={country} />
              </Container>
            </Visibility>
            <div className={cx('header')}>
              <Container>
                <Header className={cx('restrict', 'mainHeader')} color="white" elongated uppercase>
                  {!isUSUser ? 'Never Pay Full Price For Gas Again.' : (
                    <React.Fragment>
                      <Text>Most Ways, Most Places</Text>
                      <br />
                      <Text>To Save Money On Gas.</Text>
                    </React.Fragment>
                  )}
                </Header>
              </Container>
            </div>
            <Container className={cx('imageContainer')}>
              <HeroPhoneImage
                className={cx('phone')}
                isUSUser={isUSUser}
              />
            </Container>
            <Container>
              <FlexGrid>
                <FlexGrid.Column desktop={8}>
                  {!isUSUser && (
                    <Header as="h2" className={cx('lead')}>
                      Find the best gas price, anywhere.
                    </Header>
                  )}
                  <Text as="p" className={cx('sublead')}>
                    <Text as="b">{subLeadText.bold}</Text>{subLeadText.regular}
                  </Text>
                  <br />
                  <br />
                  <Button
                    as="a"
                    className={cx('mobileFluid')}
                    href={isUSUser ? `https://${payNgHost}` : 'https://routing.gasbuddy.com/App'}
                    onClick={handleEnrollClick}
                    primary
                    size="lg"
                  >
                    {isUSUser ? 'Get The Free Card' : 'Download The App'}
                  </Button>
                </FlexGrid.Column>
              </FlexGrid>
            </Container>
            <Visibility mobile tablet={false} desktop={false}>
              <HeroPhoneImage isUSUser={isUSUser} />
            </Visibility>
          </div>
          {isUSUser && (
            <Container className={cx('pwgbContainerWrapper')} fluid>
              <HomePageGasBackBrands
                brands={offers.filter(({ tags }) => tags && tags.includes('featured_web'))}
                className={cx('gasBackOffers')}
              />
            </Container>
          )}
          <Container className={cx('metricsContainer')}>
            <div className={cx('metricsOffset')}>
              <ReachMetrics platformTotals={platformTotals} country={country} />
            </div>
          </Container>
          <Divider className={cx('homeDivider')} />
          <Container>
            <Testimonials className={cx('testimonials')}>
              {testimonials.map(({ username, text, name, stars = 0 }) => (
                <TestimonialCard
                  key={name}
                  text={text}
                  from={name}
                  stars={stars}
                  saved={savedByUser[username]}
                />
              ))}
            </Testimonials>
          </Container>
          <AppPromo className={cx('homeAppPromo')} country={country} />
          <SavingsTools country={country} className={cx('savings')} />
          <Container className={cx('regionsContainer')} fluid>
            <SearchStateCloud country={country} />
          </Container>
        </div>
      </Page>
    </div>
  );
}

Homepage.propTypes = {
  country: PropTypes.oneOf(Object.values(COUNTRY_CODES)),
  offers: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    logo: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
  })),
  payNgHost: PropTypes.string,
  platformTotals: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]).isRequired,
  })),
  savedByUser: PropTypes.objectOf(PropTypes.number),
};

Homepage.defaultProps = {
  country: COUNTRY_CODES.USA,
  offers: [],
  payNgHost: 'enroll.gasbuddy.com',
  platformTotals: [],
  savedByUser: {},
};
